import { useEffect } from 'react'

import { useApi } from '@core/api/hooks/useApi'
import { customEvent, EventType } from '@core/customEvent'
import { useGlobalStore } from '@core/hooks/useGlobalStore'
import { logWarning } from '@core/logging'
import { getGlobalStore } from '@core/store'
import type { IAllWidgetParams } from '@core/types'

import {
  channelActionsSelector,
  channelBusinessActiveSelector,
  channelByIdSelector,
  channelByUsernameSelector,
} from '../model'
import type { Channel } from '../types'

export type Props = {
  username: string | undefined
  encodedId: string | undefined
  target: HTMLElement | ShadowRoot | null | undefined
  widgetConfigId?: string | undefined
  embedConfigParams?: Partial<IAllWidgetParams>
}

/**
 * Resolve channel by providing username or encoded id. Fetches channel from API if needed, then stores it in
 * globalStore for shared access between widgets. Prevents multiple API requests for the same channel.
 * @param props
 */
export function useChannel(props: Props): Channel | null {
  const { username, encodedId, target, widgetConfigId, embedConfigParams } =
    props

  const api = useApi()

  const actions = useGlobalStore(channelActionsSelector)
  const identifier = encodedId || username || embedConfigParams?.channel
  const businessActive = useGlobalStore(
    channelBusinessActiveSelector(identifier),
  )
  const channel = useGlobalStore(
    encodedId
      ? channelByIdSelector(encodedId)
      : username
        ? channelByUsernameSelector(username)
        : embedConfigParams?.channel
          ? channelByUsernameSelector(embedConfigParams.channel)
          : () => null,
  )

  useEffect(() => {
    if (businessActive !== false || !target) {
      return
    }
    const host = target instanceof ShadowRoot ? target.host : target
    if (host instanceof HTMLElement) {
      host.hidden = true
    }
  }, [businessActive, target])

  useEffect(() => {
    ;(async () => {
      try {
        if (!username && !encodedId && !widgetConfigId) {
          throw new Error(
            'Missing channel username or encodedId or widgetConfigId',
          )
        }

        if (username) {
          if (actions && !actions.markRequested(username)) {
            api
              .getChannel(username)
              .then((result) => {
                if (!identifier) {
                  return
                }
                if (result.business_active !== true) {
                  actions.setBusinessActive(identifier, false)
                  return
                }
                actions.setBusinessActive(identifier, true)
                actions.updateChannel(result.channel)
              })
              .catch((e) => {
                logWarning(`Fetching channel ${username} failed`, e)
              })
          }
        } else if (encodedId) {
          if (actions && !actions.markRequested(encodedId)) {
            api
              .getChannelById(encodedId)
              .then((result) => {
                if (!identifier) {
                  return
                }
                if (result.business_active !== true) {
                  actions.setBusinessActive(identifier, false)
                  return
                }
                actions.setBusinessActive(identifier, true)
                const { business_active: _businessActive, ...channel } = result
                actions.updateChannel(channel)
              })
              .catch((e) => {
                logWarning(`Fetching channel ${encodedId} failed`, e)
              })
          }
        }
      } catch (e) {
        customEvent({
          target,
          type: [EventType.FW_ERROR, EventType.FW_EMBEDFEED_ERROR],
          data: {
            detail: 'error',
            error: `${e}`,
          },
        })
      }
    })()
  }, [actions, api, encodedId, identifier, target, username, widgetConfigId])

  return channel || null
}

export const TEST_ONLY = {
  reset() {
    channelActionsSelector(getGlobalStore().getState())?.clearRequested()
  },
}
